
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";

import { ViewAnalytics } from "@/mixins";
import { ApiService, LogService, RouterService, SnackbarService } from "@/services";
import { ApplicationModule, getStoreModule, SessionModule } from "@/store";
import {
  BriefcaseActions,
  MetadataKeys,
  Profile as ProfileObj,
  SearchItem,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";

@Component({
  components: {
    ArchivedIcon: () => import(/* webpackChunkName: "async-modules" */ "@/components/ArchivedIcon.vue"),
    BriefcaseDialog: () => import(/* webpackChunkName: "async-modules" */ "@/components/BriefcaseFolderMoveDialog.vue"),
    ContactUsTextButton: () => import(/* webpackChunkName: "async-modules" */ "@/components/ContactUsTextButton.vue"),
    Dialog: () => import(/* webpackChunkName: "async-modules" */ "@/components/vuetify-wrappers/Dialog.vue"),
    ProfileCard: () => import(/* webpackChunkName: "async-modules" */ "@/components/ProfileCard.vue"),
    ProfileItem: () => import(/* webpackChunkName: "async-modules" */ "@/components/profile/ProfileItem.vue"),
    ProfilePanel: () => import(/* webpackChunkName: "async-modules" */ "@/components/profile/ProfilePanel.vue"),
    NotFoundComponent: () => import(/* webpackChunkName: "async-modules" */ "@/components/404.vue"),
  },
})
export default class Profile extends mixins(ViewAnalytics) {
  private applicationStore!: ApplicationModule;
  private sessionStore!: SessionModule;

  private profile: ProfileObj | null = null;
  private display404 = false;
  private lifeInBriefPanelModel = 0;
  private mainPanelModel = [0, 1, 2];
  private mainPanelsExpanded = false;
  private noAccessBannerVisible = false;
  private hasAccessToProfile = false;
  private docxBtnClicked = false;
  private pdfBtnClicked = false;
  private docxWithCustomLogoBtnClicked = false;
  private trialLimitBanner = false;
  private requestCustomLogoDialog = false;

  // Override for PageTitle mixin, updated below in 'created'
  pageName = "";

  created(): void {
    this.applicationStore = getStoreModule(ApplicationModule, this.$store);
    this.sessionStore = getStoreModule(SessionModule, this.$store);

    let profileIdParam = this.$route.params.profileId;
    let profileNameParam = this.$route.params.profileName;
    LogService.debug(`Profile.vue: profileIdParam: ${profileIdParam}, profileNameParam: ${profileNameParam}`);

    let profileId = +profileIdParam;

    if (isNaN(profileId)) {
      LogService.warn(`Profile.vue: Invalid profileId is requested: '${profileIdParam}'`);
    } else {
      ApiService.refreshBriefcase().then(() => {
        ApiService.getProfile(profileId)
          .then((profile) => {
            this.profile = profile;
            if (this.profile != null) {
              this.profile.subscriptionModules.forEach((module) => {
                if (this.sessionStore.hasVignetteModuleAccess(module)) {
                  this.hasAccessToProfile = true;
                  if (this.sessionStore.isTrialUser && this.profile != null && !this.profile.isUnlocked) {
                    this.sessionStore.setProfilesViewedDuringTrial(profileId);
                    if (
                      this.sessionStore.getProfilesViewedDuringTrial.indexOf(profileId) === -1 &&
                      this.sessionStore.hasReachedTrialLimit
                    ) {
                      this.trialLimitBanner = true;
                    }
                  }
                }
              });
              this.pageName = this.profile.preferredNameDisplay ? this.profile.preferredNameDisplay + " profile" : "";
              if (!this.hasAccessToProfile && !this.profile.isUnlocked) {
                LogService.warn(`Profile.vue: User doesn't have access to this profile.`);
                this.noAccessBannerVisible = true;
              }
            }
          })
          .catch(() => {
            LogService.warn(`Profile.vue: Error retrieving profileId: '${profileId}'`);
            this.display404 = true;
          })
          .finally(() => {
            this.setAnalyticsMetadataAndSendEvent();
          });
      });
    }
  }

  get hasVignetteAccess(): boolean {
    return this.sessionStore.hasVignetteAccess;
  }

  get hasViewedProfile(): boolean {
    return this.profile != null ? this.sessionStore.hasViewedProfileDuringTrial(this.profile.id) : false;
  }

  get isWebUser(): boolean {
    return getStoreModule(SessionModule, this.$store).isWebUser;
  }

  get isProfileVisible(): boolean {
    return !this.trialLimitBanner && (this.profile?.isUnlocked || this.hasAccessToProfile);
  }

  get hasCustomLogo(): boolean {
    return getStoreModule(SessionModule, this.$store).hasCustomLogo;
  }

  // Assuming we already validated folder is not the default folder in the UI.
  requestCustomLogo(): void {
    ApiService.postUserContact("", true).then((response) => {
      if (response.statusCode === 200) {
        LogService.info(`Profile.requestCustomLogo is successful.`);
        SnackbarService.displaySnackbar("Request for custom logo successfully submitted.", "secondary");
      } else {
        LogService.error(`Profile.requestCustomLogo failed: '${response.error_message}'`);
        SnackbarService.displaySnackbar("An error occurred, please try again.");
      }
    });

    this.requestCustomLogoDialog = false;
  }

  // Expand or collapse all expansion panels
  private togglePanels(): void {
    // Push max number of <v-expansion-panel> possible, keep in mind there could be less than 16 visible in a profile.
    this.mainPanelModel = this.mainPanelsExpanded ? [] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    this.mainPanelsExpanded = !this.mainPanelsExpanded;
  }

  private getAllCmsTags(): string {
    let result = "";

    if (this.profile != null) {
      if (!!this.profile.primaryProfileType) {
        result += this.profile.primaryProfileType;
      }
      if (this.profile.primaryProfileTypesAdditional.length > 0) {
        if (result.length > 0) result += ",";
        result += this.profile.primaryProfileTypesAdditional.join(",");
      }
      if (this.profile.secondaryProfileTypes.length > 0) {
        if (result.length > 0) result += ",";
        result += this.profile.secondaryProfileTypes.join(",");
      }
      if (this.profile.subscriptionModules.length > 0) {
        if (result.length > 0) result += ",";
        result += this.profile.subscriptionModules
          .map((module) => this.applicationStore.getSubscriptionModuleName(module))
          .join(",");
      }
    }
    return result;
  }

  setAnalyticsMetadataAndSendEvent(): void {
    this.setAnalyticsMetadata(MetadataKeys.SITESECTION, "Vignette");
    if (this.profile != null) {
      this.setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
      this.setAnalyticsMetadata(MetadataKeys.CONTENT_TITLE, this.pageName);
      this.setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, String(this.profile.id));
      this.setAnalyticsMetadata(MetadataKeys.CMS_TAGS, this.getAllCmsTags());
      this.setAnalyticsMetadata(MetadataKeys.VIGNETTE_PRIMARY_PROFILE_TYPE, this.profile.primaryProfileType);
    } else {
      this.setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Error Page");
    }
    this.pushPageViewEvent();
  }

  async toggleBriefcase(): Promise<void> {
    let result = false;
    if (this.profile) {
      const searchItem: SearchItem = {
        subscriptionModules: this.profile.subscriptionModules,
        id: this.profile.id,
        briefcaseId: this.profile.briefcaseId,
        positionSummary: this.profile.positionSummary,
        preferredNameDisplay: this.profile.preferredNameDisplay,
        title: this.profile.title,
        politicalParty: this.profile.politicalParty,
        quickSummary: this.profile.quickSummary,
        headShot: this.profile.headShot,
        addedToBriefcase: !this.profile.addedToBriefcase,
        isFormer: this.profile.isFormer,
      };

      if (this.profile.addedToBriefcase) {
        const folders = this.applicationStore.getBriefcaseFolderIdsForId(searchItem.briefcaseId);

        if (folders.length > 1) {
          this.applicationStore.setBriefcaseItemBeingMoved(searchItem);
          VignetteHelpers.Analytics.push({
            ["event"]: "briefcase-move-items",
            ["custom.gtm.itemId"]: searchItem.id,
            ["custom.gtm.itemType"]: "Profile",
          });
        } else {
          await ApiService.postBriefcaseToggle([searchItem], BriefcaseActions.REMOVE);
          VignetteHelpers.Analytics.push({
            ["event"]: "briefcase-remove-items",
            ["custom.gtm.itemId"]: searchItem.id,
            ["custom.gtm.itemType"]: "Profile",
          });
          SnackbarService.displaySnackbar(`${this.profile.preferredNameDisplay} removed from Briefcase.`, "secondary");
        }
      } else {
        result = await ApiService.postBriefcaseToggle([searchItem], BriefcaseActions.ADD);
        VignetteHelpers.Analytics.push({
          ["event"]: "briefcase-add-items",
          ["custom.gtm.itemId"]: searchItem.id,
          ["custom.gtm.itemType"]: "Profile",
        });
        if (result) {
          SnackbarService.displaySnackbar(
            `${searchItem.preferredNameDisplay} added to Briefcase.`,
            "secondary",
            3000,
            searchItem
          );
        } else {
          SnackbarService.displaySnackbar(
            `You have too many profiles in your briefcase, try moving them around.`,
            "error",
            -1,
            searchItem
          );
        }
      }
      this.profile.addedToBriefcase =
        this.applicationStore.getBriefcaseFolderIdsForId(this.profile.briefcaseId).length > 0;
    }
  }

  get facebookLink(): string {
    return this.profile && this.profile.facebookUrl ? this.profile.facebookUrl : "";
  }

  get twitterLink(): string {
    return this.profile && this.profile.twitterUsername ? `https://twitter.com/${this.profile.twitterUsername}` : "";
  }

  get twitterHandle(): string {
    return this.profile && this.profile.twitterUsername ? `@${this.profile.twitterUsername}` : "";
  }

  get instagramLink(): string {
    return this.profile && this.profile.instagramUsername
      ? `https://instagram.com/${this.profile.instagramUsername}/`
      : "";
  }

  get instagramHandle(): string {
    return this.profile && this.profile.instagramUsername ? `@${this.profile.instagramUsername}` : "";
  }

  get linkedInLink(): string {
    return this.profile && this.profile.linkedInUrl ? this.profile.linkedInUrl : "";
  }

  get websiteLink(): string {
    return this.profile && this.profile.websiteUrl ? this.profile.websiteUrl : "";
  }

  get almanacLink(): string {
    return this.profile && this.profile.almanacUrl ? this.profile.almanacUrl : "";
  }

  get modules(): number[] {
    return this.profile && this.profile.subscriptionModules ? this.profile.subscriptionModules : [];
  }

  get emailLink(): string {
    if (this.profile && this.profile.email) {
      if (this.profile.email.includes("@")) {
        return `mailto:${this.profile.email}`;
      }
      return this.profile.email;
    }
    return "";
  }

  get possessivePronoun(): string {
    let gender = this.profile && this.profile.gender ? this.profile.gender : "";
    return gender === "MALE" ? "his" : gender === "FEMALE" ? "her" : "their";
  }

  goToMyAccountPage(): void {
    RouterService.navigateToSubscriptionOptions(this.$router);
  }

  goToContactPage(): void {
    RouterService.navigateByName(this.$router, VignetteRoutes.Contact);
  }

  goToSignUpPage(): void {
    RouterService.navigateByName(this.$router, VignetteRoutes.SignUp);
  }

  clickDocxBtn(): void {
    this.docxBtnClicked = true;
    setTimeout(() => (this.docxBtnClicked = false), 4000);
  }

  clickDocxWithCustomLogoBtn(): void {
    if (this.sessionStore.hasCustomLogo) {
      this.docxWithCustomLogoBtnClicked = true;
      setTimeout(() => (this.docxWithCustomLogoBtnClicked = false), 4000);
    } else {
      this.requestCustomLogoDialog = true;
    }
  }

  clickPdfBtn(): void {
    this.pdfBtnClicked = true;
    setTimeout(() => (this.pdfBtnClicked = false), 4000);
  }

  formatCurrency(value: number): string {
    return "$" + value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  get areDownloadButtonsEnabled(): boolean {
    return (
      this.profile != null &&
      (this.profile.isUnlocked ||
        !this.sessionStore.isTrialUser ||
        (this.sessionStore.isTrialUser && !this.trialLimitBanner))
    );
  }

  get docxUrl(): string {
    return !!this.profile?.docxUrl && (this.profile.isUnlocked || this.hasAccessToProfile) ? this.profile.docxUrl : "";
  }

  get docxUrlWithCustomLogo(): string {
    return !!this.profile?.docxUrl && (this.profile.isUnlocked || this.hasAccessToProfile)
      ? `${this.profile.docxUrl}?custom_logo=True`
      : "";
  }

  get pdfUrl(): string {
    return !!this.profile?.pdfUrl && (this.profile.isUnlocked || this.hasAccessToProfile) ? this.profile.pdfUrl : "";
  }
}
